import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useMilestonesStore } from '@/stores/milestones'
import type { MilestoneKey } from '@/types/milestones'

const activeUnlockKey = ref<MilestoneKey | null>(null)
let initialized = false

export function useMilestoneUnlock() {
  const authStore = useAuthStore()
  const milestonesStore = useMilestonesStore()
  const { unackedKeys, showAllFeatures } = storeToRefs(milestonesStore)

  function pickNextUnlock(keys: MilestoneKey[]): MilestoneKey | null {
    if (showAllFeatures.value || keys.length === 0) return null
    return keys[0] ?? null
  }

  async function refreshMilestones() {
    if (!authStore.isAuthenticated) {
      activeUnlockKey.value = null
      milestonesStore.clear()
      return
    }
    await milestonesStore.fetchMilestones({ force: true })
    activeUnlockKey.value = pickNextUnlock(unackedKeys.value)
  }

  async function dismissActiveUnlock() {
    const key = activeUnlockKey.value
    if (!key) return
    activeUnlockKey.value = null
    try {
      await milestonesStore.ackMilestone(key)
    } catch {
      // Keep UX responsive even if ack fails; will retry on next fetch.
    }
    activeUnlockKey.value = pickNextUnlock(unackedKeys.value.filter((k) => k !== key))
  }

  function initMilestoneUnlock() {
    if (initialized) return
    initialized = true

    watch(
      () => authStore.isAuthenticated,
      (authenticated) => {
        if (authenticated) {
          void refreshMilestones()
          return
        }
        activeUnlockKey.value = null
        milestonesStore.clear()
      },
      { immediate: true },
    )

    watch(unackedKeys, (keys) => {
      if (showAllFeatures.value) {
        activeUnlockKey.value = null
        return
      }
      if (!activeUnlockKey.value && keys.length > 0) {
        activeUnlockKey.value = pickNextUnlock(keys)
      }
    })
  }

  return {
    activeUnlockKey,
    initMilestoneUnlock,
    refreshMilestones,
    dismissActiveUnlock,
  }
}
